import { directorLifecycle, type DirectorLifecycleInput } from '@league/shared/league-lifecycle';
import { useEffect, useReducer } from 'react';

/** Re-evaluate at lifecycle boundaries and after a sleeping/background tab returns. */
export function useDirectorLifecycle(inputs: readonly DirectorLifecycleInput[]) {
  const [, refresh] = useReducer((value: number) => value + 1, 0);
  const now = Date.now();
  const results = inputs.map((input) => directorLifecycle(input, now));
  const boundaries = results.flatMap((result) =>
    result.nextChangeAt === null ? [] : [result.nextChangeAt],
  );
  const nextChangeAt = boundaries.length > 0 ? Math.min(...boundaries) : null;

  useEffect(() => {
    if (nextChangeAt === null) return;
    // Bound the delay to handle clock changes and the browser's maximum timer delay.
    const timer = window.setTimeout(refresh, Math.max(1, Math.min(nextChangeAt - Date.now(), 60_000)));
    return () => window.clearTimeout(timer);
  }, [nextChangeAt, now]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return results;
}
